import { ArrowDownAZ, ArrowUpZA, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/v2/button";
import SearchInput from "@/components/ui/v2/search-input";
import { ROUTES } from "@/constants/routes";
import { SEGMENT_TOUR_TARGET } from "@/features/segments/tour/constants";
import { TourHelpMenu } from "@/features/tours/components/TourHelpMenu";
import { useTourTarget } from "@/features/tours/useTourTarget";
import { usePermissionMatrix } from "@/lib/hooks/usePermissionMatrix";

type SegmentListHeaderProps = {
  totalSegments: number;
  searchValue: string;
  onSearchChange: (value: string) => void;
  descending: boolean;
  onToggleSort: () => void;
};

export function SegmentListHeader({
  totalSegments,
  searchValue,
  onSearchChange,
  descending,
  onToggleSort,
}: SegmentListHeaderProps) {
  const { t } = useTranslation();
  const { canCreate } = usePermissionMatrix();
  const addRef = useTourTarget<HTMLAnchorElement>(SEGMENT_TOUR_TARGET.OVERVIEW_ADD);
  const SortIcon = descending ? ArrowUpZA : ArrowDownAZ;

  return (
    <div className="sticky top-0 z-10 flex flex-col gap-4 bg-background pb-3 pt-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold">{t("common.segments")}</h1>
          {totalSegments > 0 && (
            <span className="flex h-6 min-w-6 items-center justify-center rounded-full bg-primary px-1.5 text-xs font-semibold tabular-nums text-primary-foreground">
              {totalSegments}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <TourHelpMenu />
          {canCreate("segments") && (
            <Button variant="default" size="sm" asChild>
              <Link ref={addRef} to={ROUTES.new}>
                <Plus className="mr-1.5 size-4" />
                {t("admin.segments.addSegment", "Add Segment")}
              </Link>
            </Button>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <SearchInput
            placeholder={t("admin.segments.searchPlaceholder", "Search segments...")}
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>
        <Button variant="outline" size="icon" onClick={onToggleSort} title={t("common.sort", "Sort")}>
          <SortIcon className="size-4" />
        </Button>
      </div>
    </div>
  );
}
